import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import { groupCalendarsByRegion } from "common/holiday-calendar";
import AppInput from "components/app-input";
import AppButton from "components/app-button";

const normalize = (value) => (value || "").trim().toLowerCase();

export const AppHolidayCalendarModal = (props, ref) => {
	const dispatch = useDispatch();
	const [calendar, setCalendar] = useState();
	const [visible, setVisible] = useState(false);
	const isEdit = useMemo(() => !!calendar, [calendar]);
	const header = useMemo(() => (isEdit ? "Edit Holiday Calendar" : "Create Holiday Calendar"), [isEdit]);
	const buttonLabel = useMemo(() => (isEdit ? "Save" : "Create Holiday Calendar"), [isEdit]);
	const calendars = useMemo(() => groupCalendarsByRegion(props.records), [props.records]);
	//prettier-ignore
	const initialValues = useMemo(() => ({
		title: calendar?.title || "",
		regionCode: calendar?.regionCode || "",
		region: calendar?.region || "",
		year: calendar?.years?.[0]?.year ? calendar.years[0].year.toString() : new Date().getFullYear().toString(),
	}), [calendar]);
	const formik = useFormik({
		enableReinitialize: true,
		initialValues,
		validationSchema: yup.object({
			title: yup
				.string()
				.trim()
				.required(ERRORS.REQUIRED)
				.test("is-unique-title", "Another calendar already uses this name", function (value) {
					if (!value || isEdit) return true;

					return !calendars.some((o) => normalize(o.title) === normalize(value));
				}),
			regionCode: yup
				.string()
				.trim()
				.required(ERRORS.REQUIRED)
				.matches(/^[A-Za-z0-9-]+$/, "Use letters, numbers and dashes only")
				.test("is-unique-region-code", "Region code already in use", function (value) {
					if (!value || isEdit) return true;

					return !calendars.some((o) => normalize(o.regionCode) === normalize(value));
				}),
			region: yup
				.string()
				.trim()
				.required(ERRORS.REQUIRED),
			year: yup.string().required(ERRORS.REQUIRED).matches(/^\d{4}$/, "Please enter a 4 digit year"),
		}),
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	const onHandleShow = useCallback((data) => {
		setCalendar(data);
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		formik.resetForm();
		setCalendar();
		setVisible(false);
	}, [formik]);

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		let response = null;
		const title = values.title.trim();
		const region = values.region.trim();

		try {
			if (isEdit) {
				/* Title and region are shared across every year of the calendar, so an edit
				   updates every year's record for the region to keep them in sync. Holidays
				   are managed on the details page, so they are echoed back untouched. */
				for (const year of calendar.years) {
					await api.post.holidayCalendar.update({
						id: year.id,
						title,
						region,
						regionCode: year.regionCode,
						year: year.year,
						holidays: year.holidays || [],
					});
				}
			} else {
				await api.post.holidayCalendar.create({
					title,
					region,
					regionCode: values.regionCode.trim().toUpperCase(),
					year: parseInt(values.year),
					holidays: [],
				});
			}

			response = { regionCode: isEdit ? calendar.regionCode : values.regionCode.trim().toUpperCase() };
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			onHandleDismiss();
			dispatch(promptAlertMessage({ message: isEdit ? "Holiday calendar has been updated successfully" : "Holiday calendar has been created successfully" }));
			props.onHandleGetList();

			if (!isEdit && props.onHandleCreated) props.onHandleCreated(response.regionCode);
		}
	}, [dispatch, onHandleDismiss, calendar, formik, props, isEdit]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	return (
		<Modal classes={{ root: "app-holiday-calendar-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="holiday-calendar-modal" aria-describedby="holiday-calendar-modal">
			<div className="holiday-calendar-modal">
				<div className="holiday-calendar-modal__main">
					<div className="holiday-calendar-modal__header">
						<p className="holiday-calendar-modal__title">{header}</p>
					</div>

					<form className="holiday-calendar-modal__body" onSubmit={formik.handleSubmit}>
						{/* prettier-ignore */}
						<AppInput required type="text" name="title" label="Calendar Name" placeholder="e.g. Selangor Public Holidays" value={formik.values.title} error={formik.errors.title} touched={formik.touched.title} disabled={formik.isSubmitting} onChange={formik.handleChange} />

						<div className="holiday-calendar-modal__row">
							{/* prettier-ignore */}
							<AppInput required type="text" name="region" label="Region" placeholder="e.g. Selangor" value={formik.values.region} error={formik.errors.region} touched={formik.touched.region} disabled={formik.isSubmitting} onChange={formik.handleChange} />

							{/* prettier-ignore */}
							<AppInput required type="text" name="regionCode" label="Region Code" placeholder="e.g. SGR" value={formik.values.regionCode} error={formik.errors.regionCode} touched={formik.touched.regionCode} disabled={isEdit || formik.isSubmitting} onChange={formik.handleChange} />
						</div>

						{!isEdit && (
							/* prettier-ignore */
							<AppInput required type="number" maxLength={4} name="year" label="Year" placeholder="e.g. 2026" value={formik.values.year} error={formik.errors.year} touched={formik.touched.year} disabled={formik.isSubmitting} onChange={formik.handleChange} />
						)}

						<div className="holiday-calendar-modal__button-container">
							<AppButton type="button" label="Cancel" outline disabled={formik.isSubmitting} onClick={onHandleDismiss} />
							<AppButton type="submit" label={buttonLabel} disabled={formik.isSubmitting} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppHolidayCalendarModal));

AppHolidayCalendarModal.propTypes = {
	ref: PropTypes.object,
	records: PropTypes.array,
	onHandleCreated: PropTypes.func,
	onHandleGetList: PropTypes.func.isRequired,
};
